/**
 * The OFFLINE OUTBOX — Phase 2's keystone.
 *
 * Every edit the user makes becomes an op ({s, op, k, d}) appended here, and
 * the UI applies it optimistically. The outbox persists to IndexedDB, so a
 * reload, a crash, or a week offline loses nothing. A single drain loop sends
 * batches to the crm2_apply_ops RPC; ops are org+key upserts, so replaying a
 * batch whose response was lost is harmless (idempotent by construction).
 *
 * Invariants (each tested in tests/outbox.test.ts):
 *   1. enqueue → persist BEFORE notify: what the pill counts is on disk.
 *   2. Per-key coalescing (put absorbs patch, del clears the slot) — but
 *      NEVER across a del boundary: del→patch is a resurrection and both
 *      ops must reach the server in order.
 *   3. Drain is single-flight, preserves order, and removes exactly the ops
 *      it sent (edits made DURING a send are never lost).
 *   4. Network/5xx failure: keep everything, back off, retry forever.
 *      Definitive 4xx rejection: the batch moves to `dead` — visible, never
 *      silently dropped, never wedging the queue (v1's frozen-saves lesson).
 */
import type { HttpError } from "./http";
import { composeIntent, type LedgerIntent } from "../features/settings/ledgerIntent";

export interface RecOp {
  s: "lead_fields" | "tasks" | "deals" | "calendar" | "tombs" | "wholesale" | "saved_views" | "contacts" | "settings" | "scope_meta" | "scope_cats" | "scope_items" | "leads" | "materials" | "material_categories";
  op: "put" | "patch" | "del" | "group" | "add";
  k: string;
  /** object for put/patch; ARRAY of events for calendar's group op;
   * ARRAY of key strings for tombs add/del; ARRAY of rows for the settings
   * array blobs (v1 tuple ledgers like EXP2) + saved_view [name,flag,spec];
   * bare STRING for settings.SHOWINGTIME_FEED (Phase 27 — a url value) */
  d?: Record<string, unknown> | Record<string, unknown>[] | string[] | unknown[] | string;
  /** deals only: pipeline column index (validated 0..30 server-side) */
  stage?: number;
  /** lead_fields patch only (Phase 12): top-level d keys that REPLACE (or,
   * with a null d value, REMOVE) instead of deep-merging — both here when
   * coalescing AND server-side (the door rewrites them into puts). Needed by
   * nested state objects (compsTool.saved) where a deep merge would
   * resurrect deleted entries. */
  rep?: string[];
  /** scope_cats / scope_items only (Phase 15): the deal's nat_key — the
   * server writes it into deal_key and requires k = deal + '|' + id. */
  deal?: string;
  /** settings LEDGER puts only (Phase 26): the md5 etag of the value this
   * write is based on. The door rejects the op (crm2_stale_write) when the
   * stored ledger changed since — a stale tab can no longer resurrect rows
   * someone else removed. Optional: absent = no guard (legacy behavior). */
  base?: string;
  /** settings LEDGER puts only (2026-09-02): the row-level delta this
   * whole-array put encodes. CLIENT-ONLY — never reaches the door (sendOps
   * strips it); delivery uses it to rebuild `d` over the server's CURRENT
   * copy so a stale tab can't overwrite rows other devices added since. */
  intent?: LedgerIntent;
}

interface QueueItem extends RecOp {
  seq: number;
  at: number;
  /** globally-unique op id (tab-scoped) — the identity used for removal, so
   * two tabs draining the same shared queue converge instead of clobbering.
   * Legacy items persisted before this field get a deterministic synthetic
   * id at load ("legacy:<seq>") so every tab agrees on their identity. */
  oid?: string;
  /** dead items only: the server's rejection reason (trimmed) — surfaced in
   * the sync pill so a rejected save says WHY, not just "tell Claude". */
  err?: string;
}

interface Persisted {
  seq: number;
  items: QueueItem[];
  dead: QueueItem[];
}

/** Cross-tab mutex. Browsers: the Web Locks API — one callback at a time per
 * lock name, across ALL tabs of the origin. Tests / older engines: a module-
 * level promise chain per name (same-process serialization, which is exactly
 * what the tests simulate with two Outbox instances over one store). */
const localLocks = new Map<string, Promise<unknown>>();
function withLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const nav = typeof navigator !== "undefined" ? (navigator as { locks?: { request<R>(n: string, cb: () => Promise<R>): Promise<R> } }) : undefined;
  if (nav?.locks?.request) return nav.locks.request(name, fn);
  const tail = localLocks.get(name) ?? Promise.resolve();
  const run = tail.then(fn, fn);
  localLocks.set(name, run.then(() => undefined, () => undefined));
  return run;
}
const LOCK = "crm2_outbox_v1";

/** Storage seam: IndexedDB in the app, a Map in tests. Both async. */
export interface OutboxStore {
  get(key: string): Promise<Persisted | null>;
  set(key: string, value: Persisted): Promise<boolean>;
}

export interface OutboxSnapshot {
  pending: number;
  dead: number;
  sending: boolean;
  /** the most recent rejection reason, when anything is in `dead` */
  deadReason?: string;
}

const KEY = "outbox_v1";
const BATCH = 50;

const deepMerge = (
  a: Record<string, unknown>,
  b: Record<string, unknown>,
  /** top-level keys of b that replace wholesale instead of deep-merging */
  rep?: Set<string>,
): Record<string, unknown> => {
  const out: Record<string, unknown> = { ...a };
  for (const [k, v] of Object.entries(b)) {
    const prev = out[k];
    if (
      !rep?.has(k) &&
      v !== null && typeof v === "object" && !Array.isArray(v) &&
      prev !== null && typeof prev === "object" && !Array.isArray(prev)
    ) {
      out[k] = deepMerge(prev as Record<string, unknown>, v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out;
};

export class Outbox {
  private state: Persisted = { seq: 0, items: [], dead: [] };
  private loaded: Promise<void>;
  private sending = false;
  private fails = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private listeners = new Set<(s: OutboxSnapshot) => void>();
  /** this tab's identity — prefixes every oid it mints */
  private tab = Math.random().toString(36).slice(2, 10);

  constructor(
    private store: OutboxStore,
    private send: (ops: RecOp[]) => Promise<void>,
    private isDefinitiveReject: (e: unknown) => boolean = defaultDefinitiveReject,
  ) {
    this.loaded = withLock(LOCK, () => this.reloadLocked());
  }

  /** Re-read the shared store into memory. MUST be called inside the lock.
   * Memory is only ever mutated inside the lock and persisted before release,
   * so after this call `state` is the one true queue — including ops another
   * tab added or removed since we last looked. */
  private async reloadLocked(): Promise<void> {
    const p = await this.store.get(KEY);
    if (p && Array.isArray(p.items)) {
      const adopt = (it: QueueItem): QueueItem => (it.oid ? it : { ...it, oid: "legacy:" + it.seq });
      this.state = {
        seq: Math.max(p.seq ?? 0, this.state.seq),
        items: p.items.map(adopt),
        dead: Array.isArray(p.dead) ? p.dead.map(adopt) : [],
      };
    }
  }

  /** Resolves when the persisted queue has been read back (call at boot). */
  ready(): Promise<void> {
    return this.loaded;
  }

  snapshot(): OutboxSnapshot {
    const lastDead = this.state.dead[this.state.dead.length - 1];
    return {
      pending: this.state.items.length,
      dead: this.state.dead.length,
      sending: this.sending,
      ...(lastDead?.err ? { deadReason: lastDead.err } : {}),
    };
  }

  /** Unsent ops in send order — replayed over fetched state so a refresh
   * can never visually revert an edit that is still in the queue. */
  pendingOps(): RecOp[] {
    return this.state.items.map(({ s, op, k, d, stage, rep, base, intent }) => {
      const out: RecOp = { s, op, k };
      if (d !== undefined) out.d = d;
      if (stage !== undefined) out.stage = stage;
      if (rep !== undefined) out.rep = rep;
      if (base !== undefined) out.base = base;
      if (intent !== undefined) out.intent = intent;
      return out;
    });
  }

  subscribe(fn: (s: OutboxSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snapshot());
    return () => this.listeners.delete(fn);
  }

  private notify() {
    const s = this.snapshot();
    for (const fn of this.listeners) fn(s);
  }

  private async persist(): Promise<void> {
    await this.store.set(KEY, this.state);
  }

  /** Queue an op (coalescing per key), persist, then kick a drain.
   * The whole read-modify-write runs under the cross-tab lock: another tab's
   * queued ops are re-loaded first, so persisting can never clobber them
   * (the multi-tab silent-edit-loss hole found in the 2026-07-22 audit). */
  async enqueue(op: RecOp): Promise<void> {
    await this.loaded;
    await withLock(LOCK, async () => {
      await this.reloadLocked();
      this.enqueueLocked(op);
      await this.persist();
    });
    this.notify();
    void this.drain();
  }

  private enqueueLocked(op: RecOp): void {
    const seq = ++this.state.seq;
    const item: QueueItem = { ...op, seq, at: Date.now(), oid: this.tab + ":" + seq };
    const items = this.state.items;
    // walk back over ops for the same record; stop at any del (a boundary —
    // order across it is semantic: del then patch = tombstone then resurrect)
    let replaced = false;
    for (let i = items.length - 1; i >= 0; i--) {
      const prev = items[i];
      if (!prev || prev.s !== op.s || prev.k !== op.k) continue;
      if (op.s === "tombs") {
        // tombs k is a KIND (lead/contact/task), not an entity — add/del
        // carry independent key LISTS. Same-verb ops union; opposite verbs
        // are a boundary (add-then-del of DIFFERENT keys must both reach the
        // server in order — supersession here once ate delete-tombstones).
        if (prev.op === op.op) {
          const a = (Array.isArray(prev.d) ? prev.d : []) as string[];
          const b = (Array.isArray(op.d) ? op.d : []) as string[];
          prev.d = [...new Set([...a, ...b])];
          prev.at = item.at;
          replaced = true;
        }
        break;
      }
      if (prev.op === "del") break; // never coalesce across a del boundary
      if (op.op === "del" || op.op === "put" || op.op === "group") {
        // group = whole-group REPLACE (calendar): the newest op supersedes
        // any queued one for the same group — same rule as put/del.
        // A ledger put superseding a ledger put folds the older delta into
        // the newer one, so the merged write still carries BOTH changes.
        if (op.op === "put" && prev.op === "put" && item.intent) {
          // an older put WITHOUT a delta (queued before deltas existed) can
          // only be honored by the full array — drop ours so it replaces.
          if (prev.intent) item.intent = composeIntent(prev.intent, item.intent);
          else delete item.intent;
        }
        items.splice(i, 1); // superseded entirely
        continue;
      }
      if (prev.op === "group") break; // arrays never fold into objects
      // op is a patch onto a prior put/patch: fold it in, keep prior's slot.
      // rep keys replace instead of deep-merging; the rep union rides on a
      // folded PATCH (the server needs it too) but never on a PUT (a put
      // already replaces the whole record server-side).
      const repSet = new Set([...(prev.rep ?? []), ...(op.rep ?? [])]);
      prev.d = deepMerge(
        (prev.d ?? {}) as Record<string, unknown>,
        (op.d ?? {}) as Record<string, unknown>,
        repSet.size ? repSet : undefined,
      );
      if (prev.op === "patch" && repSet.size) prev.rep = [...repSet];
      if (op.stage !== undefined) prev.stage = op.stage; // newest stage wins
      prev.at = item.at;
      replaced = true;
      break;
    }
    if (!replaced) items.push(item);
  }

  /** Single-flight drain (per tab); safe to call from anywhere, any time.
   * The send itself runs OUTSIDE the lock (a 15s request must not block the
   * other tab's typing); the snapshot before it and the removal after it are
   * both lock-scoped reloads, so removal is by op identity (`oid`) against
   * whatever the queue holds NOW. Two tabs may race to send the same batch —
   * the door is idempotent upserts, so a double-send is harmless and the
   * second removal is a no-op. */
  async drain(): Promise<void> {
    await this.loaded;
    // Single-flight via a SHARED promise: a second caller joins the in-flight
    // drain instead of returning early. This makes `await drain()` mean what
    // callers think it means (SOW force-save awaits drain then checks
    // pendingOps — an early return could falsely report "not saved").
    if (this.draining) return this.draining;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    this.draining = this.drainLoop().finally(() => {
      this.draining = null;
    });
    return this.draining;
  }

  private draining: Promise<void> | null = null;

  private async drainLoop(): Promise<void> {
    this.sending = true;
    this.notify();
    try {
      for (;;) {
        // snapshot the head of the SHARED queue under the lock
        const batch = await withLock(LOCK, async () => {
          await this.reloadLocked();
          return this.state.items.slice(0, BATCH);
        });
        if (batch.length === 0) return;
        try {
          await this.deliver(batch);
          this.fails = 0;
        } catch {
          // network trouble / 5xx: keep everything, back off, try again later.
          // (deliver() already removed/dead-lettered whatever it resolved.)
          this.fails++;
          this.scheduleRetry();
          return; // leave sending=false via finally
        }
      }
    } finally {
      this.sending = false;
      this.notify();
    }
  }

  /** Send a batch; on a definitive reject, BISECT to isolate the poison op(s)
   * instead of dead-lettering innocents with them (2026-07-22 audit: the door
   * validates all-or-nothing, so one oversized/invalid op used to kill the
   * whole 50-op batch — 49 good edits died alongside it). Ops are idempotent
   * upserts, so re-sending a half whose sibling failed is harmless; halves go
   * in order, so per-key op order is preserved. Only genuinely-rejected ops
   * reach `dead`. Network errors THROW upward with everything unresolved
   * still safely queued (nothing is removed until its sub-batch settles). */
  private async deliver(batch: QueueItem[]): Promise<void> {
    try {
      await this.send(batch.map(({ s, op, k, d, stage, rep, deal, base, intent }) => {
        const out: RecOp = { s, op, k };
        if (d !== undefined) out.d = d;
        if (stage !== undefined) out.stage = stage;
        if (rep !== undefined) out.rep = rep;
        if (deal !== undefined) out.deal = deal;
        if (base !== undefined) out.base = base;
        if (intent !== undefined) out.intent = intent; // the sender merges + strips it
        return out;
      }));
      // delivered: remove exactly these ops — by identity, against a fresh
      // reload, so ops enqueued meanwhile (this tab OR another) survive
      const sentIds = new Set(batch.map((b) => b.oid));
      await withLock(LOCK, async () => {
        await this.reloadLocked();
        this.state.items = this.state.items.filter((i) => !sentIds.has(i.oid));
        await this.persist();
      });
      this.notify();
    } catch (e) {
      if (!this.isDefinitiveReject(e)) throw e; // network/5xx → caller backs off
      if (batch.length === 1) {
        // the server UNDERSTOOD and said no — this op can never succeed.
        // Visible, never silent: it moves to `dead` WITH the server's reason,
        // and the queue unblocks.
        const why = rejectReason(e);
        const deadIds = new Set(batch.map((b) => b.oid));
        await withLock(LOCK, async () => {
          await this.reloadLocked();
          const have = new Set(this.state.dead.map((d) => d.oid));
          const marked = batch.filter((b) => !have.has(b.oid)).map((b) => (why ? { ...b, err: why } : b));
          this.state.dead = [...this.state.dead, ...marked].slice(-200);
          this.state.items = this.state.items.filter((i) => !deadIds.has(i.oid));
          await this.persist();
        });
        this.notify();
        return;
      }
      const mid = Math.ceil(batch.length / 2);
      await this.deliver(batch.slice(0, mid));
      await this.deliver(batch.slice(mid));
    }
  }

  private scheduleRetry() {
    if (this.retryTimer) return;
    const delay = Math.min(60000, 5000 * this.fails) + Math.random() * 2000;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.drain();
    }, delay);
  }

  /** The dead list is a confession, not a trash can: callers may re-queue. */
  async clearDead(): Promise<void> {
    await this.loaded;
    await withLock(LOCK, async () => {
      await this.reloadLocked();
      this.state.dead = [];
      await this.persist();
    });
    this.notify();
  }

  /** The dead list, readable: what was rejected and why — powers the sync-pill
   * inspector so a rejected save is self-diagnosable instead of "tell Claude". */
  deadOps(): { s: string; op: string; k: string; err?: string }[] {
    return this.state.dead.map(({ s, op, k, err }) => ({ s, op, k, ...(err ? { err } : {}) }));
  }

  /** Second chance: move every dead op back to the FRONT of the live queue
   * (fresh oids, reasons stripped — original rejection order kept) and drain.
   * Rejects that were conditional — a door rule since fixed (PARTNERS before
   * Phase 26), a rate blip miscategorized — just deliver now; ops the server
   * still refuses simply return to `dead` with a fresh reason. Stale-write
   * guards (`base`) are kept as-is on purpose: a re-reject is harmless, but
   * force-stripping them could resurrect rows someone else deleted. */
  async retryDead(): Promise<void> {
    await this.loaded;
    await withLock(LOCK, async () => {
      await this.reloadLocked();
      if (this.state.dead.length === 0) return;
      const revived = this.state.dead.map((d) => {
        const seq = ++this.state.seq;
        const { err: _err, ...rest } = d;
        return { ...rest, seq, at: Date.now(), oid: this.tab + ":" + seq };
      });
      this.state.items = [...revived, ...this.state.items];
      this.state.dead = [];
      await this.persist();
    });
    this.notify();
    void this.drain();
  }

  stop() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}

/** A definitive reject = the server answered with a 4xx that is not an auth
 * hiccup. 401/403 mean "token stale" — auth repair fixes those, so retry. */
export function defaultDefinitiveReject(e: unknown): boolean {
  const status = (e as HttpError)?.status;
  return typeof status === "number" && status >= 400 && status < 500 && status !== 401 && status !== 403 && status !== 429;
}

/** Human-readable reason out of a door rejection. PostgREST surfaces a raised
 * exception as { message: "crm2_bad_ops: …" }; fall back to the error's own
 * message. Trimmed hard — this rides in the pill, not a log file. */
export function rejectReason(e: unknown): string {
  const body = (e as HttpError)?.body;
  const msg =
    body !== null && typeof body === "object" && typeof (body as { message?: unknown }).message === "string"
      ? (body as { message: string }).message
      : e instanceof Error
        ? e.message
        : "";
  return msg.replace(/^crm2_bad_ops:\s*/i, "").trim().slice(0, 140);
}
